// src/store/slices/roleSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import roleApi from 'api/roleApi';

/* ------------------------------------------------------------------ */
/* Async Thunks */
/* ------------------------------------------------------------------ */

export const fetchRoles = createAsyncThunk(
  'role/fetchRoles',
  async ({ page = 1, pageSize = 10, search = '', is_active }, { rejectWithValue }) => {
    try {
      const response = await roleApi.getAll(page, pageSize, search, is_active);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load roles';
      return rejectWithValue(msg);
    }
  }
);

export const createRole = createAsyncThunk(
  'role/createRole',
  async (data, { rejectWithValue }) => {
    try {
      const response = await roleApi.create(data);
      return response;
    } catch (err) {
      const fieldErrors = err.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(', ');
        return rejectWithValue(messages);
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to create role');
    }
  }
);

export const updateRole = createAsyncThunk(
  'role/updateRole',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await roleApi.update(id, updates);
      return response;
    } catch (err) {
      const fieldErrors = err.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(', ');
        return rejectWithValue(messages);
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to update role');
    }
  }
);

export const deleteRole = createAsyncThunk(
  'role/deleteRole',
  async (id, { rejectWithValue }) => {
    try {
      await roleApi.delete(id);
      return id;
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.detail || 'Failed to delete role';
      return rejectWithValue(msg);
    }
  }
);

export const duplicateRole = createAsyncThunk(
  'role/duplicateRole',
  async ({ id, name, level }, { rejectWithValue }) => {
    try {
      const response = await roleApi.duplicate(id, { name, level });
      return response;
    } catch (err) {
      const fieldErrors = err.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors[0] : errors}`)
          .join(', ');
        return rejectWithValue(messages);
      }
      return rejectWithValue(err.response?.data?.detail || 'Failed to duplicate role');
    }
  }
);

/* ------------------------------------------------------------------ */
/* Slice */
/* ------------------------------------------------------------------ */

const roleSlice = createSlice({
  name: 'role',
  initialState: {
    roles: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* FETCH */
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      /* CREATE */
      .addCase(createRole.fulfilled, (state, action) => {
        state.roles.unshift(action.payload);
        state.pagination.count += 1;
      })

      /* UPDATE */
      .addCase(updateRole.fulfilled, (state, action) => {
        const idx = state.roles.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.roles[idx] = action.payload;
      })

      /* DELETE */
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter((r) => r.id !== action.payload);
        state.pagination.count -= 1;
      })

      /* DUPLICATE */
      .addCase(duplicateRole.fulfilled, (state, action) => {
        state.roles.unshift(action.payload);
        state.pagination.count += 1;
      });
  },
});

export const { clearError } = roleSlice.actions;
export default roleSlice.reducer;